import { Button } from './';
import { useMe } from '../hooks/useMe';
import { formatDistance } from '../lib/units';
import { Users, Clock, MapPin, CheckCircle, Lock, AlertTriangle, XCircle } from 'lucide-react';

interface EventMetadata {
  startTime: Date;
  userRsvpStatus: 'yes' | 'no' | 'maybe' | null;
  rsvpCount: number;
  distanceM?: number | null; // Issue #1083: meters canonical unit
  registrationStatus?: 'open' | 'closed' | 'full' | 'cancelled';
}

interface NextMoveCardProps {
  title: string;
  subtitle: string;
  time?: string; // Optional time display
  event?: EventMetadata; // Enhanced event data for metadata display
  primaryAction?: {
    label: string;
    onClick: () => void;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  backgroundImage?: string;
  className?: string;
}

const NextMoveCard = ({
  title,
  subtitle,
  event,
  primaryAction,
  secondaryAction,
  backgroundImage,
  className = ''
}: NextMoveCardProps) => {
  // Get user's distance unit preference (Issue #1083)
  const { data: meData } = useMe();
  const distanceUnit = meData?.distance_unit || 'mi';

  // Helper function to format time
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Get RSVP status indicator
  const getRsvpStatusIcon = (status: string | null, isUser: boolean = false) => {
    if (isUser && (status === 'yes' || status === 'maybe')) {
      return <CheckCircle className="w-4 h-4 text-oro-success" />;
    }
    return <Users className="w-4 h-4" />;
  };

  // Get registration status
  const getRegistrationStatusIcon = (status: string) => {
    switch (status) {
      case 'open': return <CheckCircle className="w-4 h-4 text-oro-success" />;
      case 'closed': return <Lock className="w-4 h-4" />;
      case 'full': return <AlertTriangle className="w-4 h-4 text-oro-warning" />;
      case 'cancelled': return <XCircle className="w-4 h-4 text-oro-error" />;
      default: return <CheckCircle className="w-4 h-4 text-oro-success" />;
    }
  };

  return (
    <div 
      className={`relative overflow-hidden rounded-3xl p-5 ${className}`}
      style={{
        backgroundColor: 'var(--oro-surface-alt)',
        boxShadow: 'var(--shadow-floating)',
        border: '1px solid var(--oro-border)',
        ...(backgroundImage ? {
          background: `linear-gradient(135deg, rgba(12, 19, 16, 0.85) 0%, rgba(19, 26, 23, 0.9) 50%, rgba(26, 34, 31, 0.85) 100%), url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        } : {
          background: 'linear-gradient(135deg, var(--oro-surface) 0%, var(--oro-surface-alt) 50%, var(--oro-surface) 100%)'
        })
      }}
    >
      {/* Subtle topo overlay for texture */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
        <svg 
          width="100%" 
          height="100%" 
          viewBox="0 0 400 300" 
          className="w-full h-full"
        >
          <path 
            d="M0,100 Q100,80 200,100 T400,100"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
          />
          <path 
            d="M0,150 Q150,130 300,150 T400,150"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
          />
          <path 
            d="M0,200 Q100,220 200,200 T400,200"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
          />
        </svg>
      </div>

      <div className="relative z-10 space-y-4">
        <div className="space-y-2">
          <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
            {title}
          </h2>
          <p className="text-base" style={{ color: 'var(--text-secondary)' }}>
            {subtitle}
          </p>
        </div>

        {/* Event Metadata Icons */}
        {event && (
          <div className="flex flex-wrap gap-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
            {/* RSVP Count */}
            <div className="flex items-center gap-1.5">
              {getRsvpStatusIcon(event.userRsvpStatus, true)}
              <span>{event.rsvpCount} Going</span>
            </div>

            {/* Event Time */}
            <div className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              <span>{formatTime(event.startTime)}</span>
            </div>

            {/* Distance (if available) - Issue #1083: meters canonical unit */}
            {event.distanceM && (
              <div className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4" />
                <span>{formatDistance(event.distanceM, distanceUnit)}</span>
              </div>
            )}

            {/* Registration Status - only shown when explicitly provided (#1797) */}
            {event.registrationStatus && (
              <div className="flex items-center gap-1.5">
                {getRegistrationStatusIcon(event.registrationStatus)}
                <span className="capitalize">{event.registrationStatus}</span>
              </div>
            )}
          </div>
        )}

        {(primaryAction || secondaryAction) && (
          <div className="flex gap-3 pt-2">
            {primaryAction && (
              <Button
                variant="primary"
                onClick={primaryAction.onClick}
                className="transition-all hover:brightness-110"
                style={{
                  backgroundColor: 'var(--oro-gold)',
                  color: 'var(--oro-bg)',
                  border: 'none'
                }}
              >
                {primaryAction.label}
              </Button>
            )}
            {secondaryAction && (
              <Button
                variant="secondary"
                onClick={secondaryAction.onClick}
                className="transition-all"
                style={{
                  border: '1px solid var(--oro-border-hover)',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--oro-surface)'
                }}
              >
                {secondaryAction.label}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NextMoveCard;